import styled, { css } from "styled-components";
import FileIcon from "../icons/file";
import React from "react";
import Size from "../file-size";
import defaultTheme from "../../../../theme/theme-test-override";
import { remCalc } from "../../../../theme/style-utils-coras-nua";

const Button = styled.button`
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0 auto;
    background: transparent;
    border: none;
    min-width: 0;
    font-weight: ${props => props.theme.fontWeights.regular};
    width: ${remCalc(60)};
    height: ${remCalc(60)};
    padding: 0;
    text-align: right;
    position: absolute;
    top: 0;
    right: ${remCalc(16)};
`;
Button.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

const Description = styled.div`
    margin: 0 ${remCalc(8)} ${remCalc(8)};
    font-size: ${remCalc(12)};
    font-weight: ${props => props.theme.fontWeights.regular};
`;
Description.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

const Error = styled.div`
    font-size: ${remCalc(16)};
    color: ${props => props.theme.colors.alert};
    margin-top: ${remCalc(4)};
    + input {
        border: ${remCalc(1)} solid red;
    }
`;
Error.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

const FieldWrapper = styled.div`
    font-family: ${props => props.theme.fonts.fontFamily};
    margin: 0;
    color: ${props => props.theme.colors.monotoneDark};
    ${({ disabled }) => disabled && `opacity: 0.5;`};
    position: relative;
    &.hide {
        visibility: hidden;
        opacity: 0;
        position: fixed;
        width: ${remCalc(1)};
        height: ${remCalc(1)};
    }
`;
FieldWrapper.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

const FileInfo = ({ file = new File([], "undefined") }) => (
    <KeyPairs>
        <Key>{file && file.name}</Key>
        <Value as="dd">{`${Size.Kb(file).toFixed(0)}KB`}</Value>
    </KeyPairs>
);

const FileField = ({
    id,
    label,
    description,
    error,
    onChange,
    onDelete,
    cssClass,
    defaultValue: _,
    value: file,
    ...props
}) => {
    let uniqueId = `file-input-${id}`;

    if (props.multipleFiles && props.identifier !== undefined) {
        uniqueId += `-${props.identifier}`;
        cssClass += " u-mb-2";
    }

    return (
        <FieldWrapper {...props} className={`${cssClass} inputWrapper`}>
            <Input
                {...props}
                id={uniqueId}
                type="file"
                onChange={e => onChange(e.target.files)}
            />
            {file ? (
                <>
                    <Label htmlFor={uniqueId} selected={true} {...props}>
                        <FileIcon file={file} />
                        <FileInfo file={file} />
                    </Label>
                    <Button
                        type="button"
                        onClick={onDelete}
                        disabled={props.disabled}
                        aria-label={"Delete" + file.name}
                    >
                        Delete
                    </Button>
                </>
            ) : (
                <Label htmlFor={uniqueId} {...props}>
                    <FileIcon file={file} />
                    <strong>Browse or take a photo</strong>
                </Label>
            )}
            {props.accept && (
                <Description
                    {...props}
                    className="u-ml-0 u-mt-2 u-ml-1 acceptedFileTypes"
                >
                    Files must be {props.accept.replace(/,(?=[^,]*$)/, " or")}{" "}
                    format.
                </Description>
            )}
            {error && <Error>{error}</Error>}
        </FieldWrapper>
    );
};

const ImageWrapper = styled(FieldWrapper)`
    align-content: center;
    justify-content: center;
    display: flex;
    border: ${remCalc(1)} solid ${props => props.theme.colors.monotoneMidLight};
    padding: ${remCalc(24)};
    margin-bottom: ${remCalc(32)};
    background-size: contain;
    background-position: center center;
    max-height: ${remCalc(300)};
    background-repeat: no-repeat;
    min-height: ${remCalc(1)};
    max-width: 100%;
    & > img {
        max-width: 100%;
        object-fit: contain;
        flex: 0 0 100%;
    }
`;
ImageWrapper.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

const ImagePreview = ({ url, description, message, ...props }) => (
    <ImageWrapper {...props}>
        {url && (
            <img src={url.replace(/^(javascript:)/, "")} alt={description} />
        )}
        {message && message}
    </ImageWrapper>
);

const Input = styled.input`
    width: ${remCalc(0.1)};
    height: ${remCalc(0.1)};
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
    &:focus {
        + label {
            outline: -moz-mac-focusring solid ${remCalc(2)};
            @media (-webkit-min-device-pixel-ratio: 0) {
                outline: -webkit-focus-ring-color auto ${remCalc(5)};
            }
        }
    }
`;

const Key = styled.dt`
    flex-flow: row;
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const KeyPairs = styled.dl`
    text-align: left;
    margin-left: ${remCalc(8)};
    max-width: calc(100% - ${remCalc(90)});
`;

const Label = styled.label`
    height: ${remCalc(60)};
    padding: 0 ${remCalc(16)};
    margin: 0;
    border-radius: ${remCalc(0)};
    cursor: ${props => (!props.disabled ? "pointer" : "default")};
    border: ${remCalc(1)} ${({ selected }) => (selected ? "solid" : "dashed")}
        ${({ disabled, selected }) =>
            selected
                ? props => props.theme.colors.monotoneMid
                : disabled
                ? props => props.theme.colors.monotoneMidLight
                : props => props.theme.colors.accent8};
    text-decoration: none;
    text-align: center;
    min-width: ${remCalc(150)};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: left;
    font-size: ${remCalc(14)};
    font-weight: ${props => props.theme.fontWeights.regular};
    & > svg {
        height: ${remCalc(18)};
        margin-right: ${remCalc(8)};
        color: ${props => props.theme.colors.accent8};
        max-width: ${remCalc(14)};
        min-width: ${remCalc(14)};
        width: ${remCalc(14)};
    }

    ${props =>
        props.primary &&
        css`
            font-size: ${remCalc(16)};
            background: ${props => props.theme.colors.primary};
            border-color: ${props => props.theme.colors.primary};
            color: white;
            font-weight: 600;
            border-radius: ${remCalc(2)};
            justify-content: center;
            &:hover {
                border-color: ${props => props.theme.colors.primaryTone};
                background: ${props => props.theme.colors.primaryTone};
            }
        `}
    ${props =>
        props.secondary &&
        css`
            font-size: ${remCalc(16)};
            background: none;
            border-radius: ${remCalc(2)};
            border-color: ${props => props.theme.colors.primary};
            color: ${props => props.theme.colors.primary};
            justify-content: center;
            font-weight: 600;
            &:hover {
                border-color: ${props => props.theme.colors.primaryTone};
                color: ${props => props.theme.colors.primaryTone};
                background: none;
            }
        `}
`;
Label.defaultProps = {
    theme: {
        ...defaultTheme
    }
};
const MultiFileWrapper = styled.div`
    order: 3;
    .inputWrapper {
        .acceptedFileTypes {
            display: none;
        }
    }
    .inputWrapper:last-child {
        .acceptedFileTypes {
            display: block;
        }
    }
`;

const SimpleFileField = ({
    id,
    label,
    description,
    error,
    onChange,
    onDelete,
    className,
    value: file,
    ...props
}) => (
    <FieldWrapper {...props}>
        <Label htmlFor={id} selected={true} {...props}>
            {label}
        </Label>
        <Input
            id={id}
            type="file"
            onChange={e => onChange(e.target.files)}
            {...props}
        />
        {error && <Error>{error}</Error>}
    </FieldWrapper>
);

const Value = styled.dd`
    font-size: ${remCalc(12)};
    font-weight: ${props => props.theme.fontWeights.light};
    margin-left: 0;
`;
Value.defaultProps = {
    theme: {
        ...defaultTheme
    }
};

export {
    Button,
    Description,
    Error,
    FieldWrapper,
    FileInfo,
    FileField,
    ImagePreview,
    ImageWrapper,
    Input,
    Key,
    KeyPairs,
    Label,
    SimpleFileField,
    MultiFileWrapper,
    Value
};
